import { storesAPI } from "@api/main";
import Product from "@models/product";
import { action, flow, makeObservable, observable } from "mobx";
import { showToastFromStore } from "@utils/storeToast";
import { analytics } from "../services/analytics";

export default class WishlistStore {
  wishlist: Product[] = [];
  isLoading: boolean = false;
  error: string | null = null;

  private _fetchWishlistPromise: Promise<Product[]> | null = null;

  constructor() {
    makeObservable(this, {
      wishlist: observable,
      isLoading: observable,
      error: observable,
      fetchWishlist: flow,
      addProductToWishlist: flow,
      removeProductFromWishlist: flow,
      clearError: action,
      clearWishlist: action,
    });
  }

  *fetchWishlist() {
    if (!this._fetchWishlistPromise) this._fetchWishlistPromise = storesAPI.getWishlist();
    const promise = this._fetchWishlistPromise;
    try {
      const res: Product[] = yield promise;
      this.wishlist = res;
    } finally {
      if (this._fetchWishlistPromise === promise) this._fetchWishlistPromise = null;
    }
  }

  /** `true` if item is on the wishlist after this call (added or already there); `null` if the API add failed. */
  *addProductToWishlist(product: Product): Generator<any, boolean | null, any> {
    if (this.wishlist.find((p) => p.id === product.id)) {
      return true;
    }
    this.isLoading = true;
    try {
      const result = yield storesAPI.addToWishlist(product.id);
      if (result.message.trim()) {
        showToastFromStore(
          result.message,
          result.ok ? "success" : "warning",
          5000,
          result.ok ? { variant: "wishlist" } : undefined
        );
      }
      if (result.ok) {
        this.wishlist.push(product);
        // Store-level choke point: covers every add surface (cards,
        // collections, product page) with one emit.
        analytics.track("wishlist_added", {
          product_id: product.id,
          product_title: product.title,
        });
        return true;
      }
      return null;
    } catch (error: any) {
      console.error("Error adding to wishlist:", error);
      this.error = error?.message ?? null;
      return null;
    } finally {
      this.isLoading = false;
    }
  }

  *removeProductFromWishlist(product: Product) {
    if (this.wishlist.find((p) => p.id === product.id)) {
      this.isLoading = true;
      try {
        yield storesAPI.removeFromWishlist(product.id);
        this.wishlist = this.wishlist.filter((p) => p.id !== product.id);
        analytics.track("wishlist_removed", {
          product_id: product.id,
          product_title: product.title,
        });
      } catch (error: any) {
        this.error = error.message;
      } finally {
        this.isLoading = false;
      }
    }
  }

  clearError() {
    this.error = null;
  }

  clearWishlist() {
    this.wishlist = [];
    this.error = null;
    this.isLoading = false;
  }
}
